// Página AdminAlertsSettings - Configuración de alertas
// Permite configurar las condiciones y umbrales para las alertas del sistema

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAlertsSettings } from '../../hooks/useAlerts'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import './AdminAlertsSettings.css'

function AdminAlertsSettings() {
  const { settings, loading, update } = useAlertsSettings()
  const [saving, setSaving] = useState(false)
  const [notification, setNotification] = useState(null)
  
  const [formData, setFormData] = useState({
    enabled: true,
    deliveryDateWarningDays: 3,
    overdueDeliveryDays: 0,
    pendingPaymentDays: 7,
    pendingReceiptDays: 7,
    pendingConfirmationDays: 2
  })

  useEffect(() => {
    if (settings?.purchaseOrders) {
      setFormData({
        enabled: settings.purchaseOrders.enabled !== false,
        deliveryDateWarningDays: settings.purchaseOrders.deliveryDateWarningDays || 3,
        overdueDeliveryDays: settings.purchaseOrders.overdueDeliveryDays || 0,
        pendingPaymentDays: settings.purchaseOrders.pendingPaymentDays || 7,
        pendingReceiptDays: settings.purchaseOrders.pendingReceiptDays || 7,
        pendingConfirmationDays: settings.purchaseOrders.pendingConfirmationDays || 2
      })
    }
  }, [settings])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : parseInt(value) || 0
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (saving) return

    setSaving(true)
    try {
      await update({
        purchaseOrders: formData
      })
      setNotification({
        type: 'success',
        message: 'Configuración de alertas actualizada correctamente'
      })
      setTimeout(() => setNotification(null), 3000)
    } catch (error) {
      console.error('Error guardando configuración:', error)
      setNotification({
        type: 'error',
        message: `Error al guardar: ${error.message || 'Error desconocido'}`
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="admin-alerts-settings-page">
        <div className="loading-state">
          <p>Cargando configuración...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-alerts-settings-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      <div className="alerts-settings-header">
        <div>
          <Link to="/admin/configuraciones">
            <button className="back-button">← Volver</button>
          </Link>
          <h1>Configuración de Alertas</h1>
          <p className="alerts-settings-subtitle">
            Configura las condiciones y umbrales para las alertas de pedidos de compra
          </p>
        </div>
      </div>

      <div className="alerts-settings-form-section">
        <form onSubmit={handleSubmit} className="alerts-settings-form">
          {/* Habilitar/Deshabilitar alertas */}
          <div className="form-section">
            <h3>Estado de las Alertas</h3>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="enabled"
                  checked={formData.enabled}
                  onChange={handleChange}
                  disabled={saving}
                />
                <span>Habilitar alertas de pedidos de compra</span>
              </label>
              <small className="module-reference">
                📦 Módulo: <strong>Compras</strong>
              </small>
            </div>
          </div>

          {/* Configuración de alertas */}
          <div className="form-section">
            <h3>Umbrales de Alertas</h3>
            
            <div className="form-group">
              <label htmlFor="deliveryDateWarningDays">
                Días de advertencia para fecha de entrega
                <span className="module-reference">📦 Módulo: <strong>Compras</strong></span>
              </label>
              <input
                type="number"
                id="deliveryDateWarningDays"
                name="deliveryDateWarningDays"
                value={formData.deliveryDateWarningDays}
                onChange={handleChange}
                min="0"
                max="30"
                disabled={saving || !formData.enabled}
                className="form-input"
              />
              <small className="form-hint">
                Alertar cuando falten X días o menos para la fecha de entrega esperada
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="overdueDeliveryDays">
                Días para alertar entrega vencida
                <span className="module-reference">📦 Módulo: <strong>Compras</strong></span>
              </label>
              <input
                type="number"
                id="overdueDeliveryDays"
                name="overdueDeliveryDays"
                value={formData.overdueDeliveryDays}
                onChange={handleChange}
                min="0"
                max="30"
                disabled={saving || !formData.enabled}
                className="form-input"
              />
              <small className="form-hint">
                Si es 0, alertará inmediatamente cuando pase la fecha de entrega. Si es mayor a 0, alertará después de X días de vencida.
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="pendingConfirmationDays">
                Días para alertar confirmación pendiente
                <span className="module-reference">📦 Módulo: <strong>Compras</strong></span>
              </label>
              <input
                type="number"
                id="pendingConfirmationDays"
                name="pendingConfirmationDays"
                value={formData.pendingConfirmationDays}
                onChange={handleChange}
                min="0"
                max="30"
                disabled={saving || !formData.enabled}
                className="form-input"
              />
              <small className="form-hint">
                Alertar cuando un pedido esté pendiente de confirmación por más de X días
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="pendingPaymentDays">
                Días para alertar pago pendiente
                <span className="module-reference">📦 Módulo: <strong>Compras</strong></span>
              </label>
              <input
                type="number"
                id="pendingPaymentDays"
                name="pendingPaymentDays"
                value={formData.pendingPaymentDays}
                onChange={handleChange}
                min="0"
                max="60"
                disabled={saving || !formData.enabled}
                className="form-input"
              />
              <small className="form-hint">
                Alertar cuando haya pagos pendientes por más de X días desde la confirmación
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="pendingReceiptDays">
                Días para alertar recepción pendiente
                <span className="module-reference">📦 Módulo: <strong>Compras</strong></span>
              </label>
              <input
                type="number"
                id="pendingReceiptDays"
                name="pendingReceiptDays"
                value={formData.pendingReceiptDays}
                onChange={handleChange}
                min="0"
                max="60"
                disabled={saving || !formData.enabled}
                className="form-input"
              />
              <small className="form-hint">
                Alertar cuando haya productos pendientes de recibir por más de X días desde el pago
              </small>
            </div>
          </div>

          <div className="form-actions">
            <Button
              type="button"
              variant="outline"
              onClick={() => window.history.back()}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={saving}
            >
              {saving ? 'Guardando...' : 'Guardar Configuración'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AdminAlertsSettings

